
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, Spinner } from "react-bootstrap";
// ✅ Import followUserApi and checkFollowStatusApi
import { fetchDirectorProfileApi, fetchPublicAppliedRoleListApi, followUserApi, checkFollowStatusApi } from "../../services/api";
import Swal from 'sweetalert2';

import ModernAuditionCard from "../../components/ModernAuditionCard";
import { TalentAuditionCard } from "../dashboard/TalentAuditionCard";

import "./CastingDirectorProfile.css";

const CastingDirectorProfile = () => {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [appliedData, setAppliedData] = useState([]); 
  const [loading, setLoading] = useState(true);

  // ✅ Description truncate state
  const [expandedDesc, setExpandedDesc] = useState(false);
  // ✅ Follow/Unfollow state
  const [isFollowed, setIsFollowed] = useState(false);

  // ✅ Handle follow/unfollow toggle using Backend API
  const handleFollowToggle = async () => {
    const token = localStorage.getItem("authToken");
    
    // ✅ 1. Check if user is logged in (Token check) - English warning message
    if (!token) {
      Swal.fire({
        icon: 'warning',
        title: 'Login Required',
        text: 'You cannot follow this Casting Director without logging in. Please sign in to continue.',
        width: '400px',
        background: '#131824',
        color: '#ffffff',
        confirmButtonColor: '#dc3545',
        customClass: {
          title: 'text-white fw-bold fs-5 mb-2',
          htmlContainer: 'text-muted mb-3 fs-6',
          confirmButton: 'btn btn-danger px-4 py-2 fw-bold'
        },
        buttonsStyling: false
      });
      return;
    }

    try {
      const res = await followUserApi(id);
      
      if (res.data?.success || res.success) {
        const nowFollowed = res.data?.followed ?? res.followed;
        setIsFollowed(nowFollowed);
        
        Swal.fire({
          icon: 'success',
          title: nowFollowed ? 'Followed!' : 'Unfollowed',
          text: nowFollowed 
            ? `You are now following ${profile?.full_name || 'this casting director'}.`
            : `You have unfollowed ${profile?.full_name || 'this casting director'}.`,
          width: '380px',
          background: '#131824',
          color: '#ffffff',
          confirmButtonColor: '#dc3545',
          customClass: {
            title: 'text-white fw-bold fs-5 mb-2',
            htmlContainer: 'text-muted mb-3 fs-6',
            confirmButton: 'btn btn-danger px-4 py-2 fw-bold'
          },
          buttonsStyling: false
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed',
          text: res.data?.message || 'Could not update follow status.',
          confirmButtonColor: '#dc3545'
        });
      }
    } catch (err) {
      console.error("Error toggling follow:", err);
      Swal.fire({
        icon: 'error',
        title: 'Action Failed',
        text: err.response?.data?.message || 'Something went wrong.',
        confirmButtonColor: '#dc3545'
      });
    }
  };

  useEffect(() => {
    loadAllData();
  }, [id]);

  // ✅ Load profile, applied roles & check follow status only if token is present
  const loadAllData = async () => {
    try {
      setLoading(true);
      const resProfile = await fetchDirectorProfileApi(id);
      const resApplied = await fetchPublicAppliedRoleListApi(id);

      if (resProfile?.data?.success) setProfile(resProfile.data.data);
      if (resApplied?.data?.success) setAppliedData(resApplied.data.data || []);

      // ✅ Call check status API ONLY if user is logged in (token exists)
      const token = localStorage.getItem("authToken");
      if (token) {
        try {
          const resStatus = await checkFollowStatusApi(id);
          if (resStatus.data?.success || resStatus.success) {
            setIsFollowed(resStatus.data?.followed ?? resStatus.followed);
          }
        } catch (fErr) {
          console.log("Error checking follow status", fErr.message);
        }
      } else {
        // Guest user by default is not following
        setIsFollowed(false);
      }

    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatOngoingData = (project, matchedRole) => ({
    id: matchedRole?.id || project?.id,
    jogetRoleId: matchedRole?.joget_role_id || matchedRole?.id,
    urlSlug: matchedRole?.urlSlug || project?.project_name?.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
    projectName: project?.project_name || "Project",
    roleTitle: matchedRole?.role_name || "View Roles",
    roleCategory: project?.project_type || "Production",
    projectImageUrl: project?.project_image || "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba",
    auditionType: matchedRole?.audition_type || matchedRole?.auditionType || "Hindi",
    roleCost: { totalAmount: project?.final_amount || matchedRole?.final_amount || 0 },
    finalAmount: project?.final_amount || matchedRole?.final_amount || 0
  });

  if (loading) {
    return (
      <div className="cdp-loading-wrap">
        <Spinner animation="border" variant="danger" />
        <p className="mt-3 text-white">Loading Profile...</p>
      </div>
    );
  }

  const selectedAuditions = appliedData.filter(item => (item.result || "").toUpperCase() === "SELECTED");
  const ongoingProjects = profile?.projects || [];

  // ✅ Truncate logic helper variables
  const aboutText = profile?.about_agency || "";
  const shouldTruncate = aboutText.length > 250;
  const truncatedText = shouldTruncate && !expandedDesc 
    ? aboutText.substring(0, 250) + "..." 
    : aboutText;

  return (
    <div className="cdp-page">
      <Container fluid className="px-3 px-md-5">

        {/* HEADER SECTION */}
        <div className="cdp-header position-relative">
          <img
            src={profile?.profile_image_url || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
            alt={profile?.full_name}
            className="cdp-image"
          />
          <div className="cdp-info flex-grow-1">
            {/* ✅ Restyled flex layout with perfect gap-4 and button margins */}
            <div className="d-flex align-items-center gap-4 mb-2 flex-wrap">
              <h1 className="mb-0">{profile?.full_name}</h1>
              {/* ✅ Follow / Unfollow Button */}
              <button 
                onClick={handleFollowToggle}
                className={`btn fw-bold px-3 py-1.5 ${isFollowed ? 'btn-outline-light' : 'btn-danger'}`}
                style={{ borderRadius: '25px', minWidth: '100px', fontSize: '0.85rem', transition: 'all 0.2s ease-in-out', marginLeft: '10px' }}
              >
                {isFollowed ? 'Following' : 'Follow'}
              </button>
            </div>
            <h3>
              {(profile?.specialization || "castingDirector")
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase())}
            </h3>
            {/* ✅ Truncated Description with inline View More / View Less */}
            <p className="cdp-about">
              {truncatedText}
              {shouldTruncate && (
                <span 
                  onClick={() => setExpandedDesc(!expandedDesc)} 
                  className="text-danger ms-2" 
                  style={{ cursor: 'pointer', fontWeight: 'bold', textDecoration: 'underline', fontSize: '0.85rem' }}
                >
                  {expandedDesc ? "View Less" : "View More"}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* SECTION 1: ONGOING PROJECTS (HORIZONTAL SCROLL) */}
        <div className="cdp-section mt-5">
          <h2 className="cdp-section-title">
            ONGOING <span>PROJECTS</span>
          </h2>
          <div className="cdp-scroll-wrapper mt-4">
            {ongoingProjects.length > 0 ? (
              ongoingProjects.map((project) => {
                const matchedRole = profile?.roles?.find(r => r.project_joget_id === project.joget_project_id);
                return (
                  <div className="cdp-scroll-item" key={`ong-${project.id}`}>
                    <ModernAuditionCard audition={formatOngoingData(project, matchedRole)} />
                  </div>
                );
              })
            ) : (
              <p className="text-white-50">No ongoing projects found.</p>
            )}
          </div>
        </div>

        {/* SECTION 2: SELECTED AUDITIONS (HORIZONTAL SCROLL) */}
        {selectedAuditions.length > 0 && (
          <div className="cdp-section mt-5 pt-4 border-top border-dark">
            <h2 className="cdp-section-title">
              SELECTED <span>AUDITIONS</span>
            </h2>
            <div className="cdp-scroll-wrapper mt-4">
              {selectedAuditions.map((item) => (
                <div className="cdp-scroll-item" key={`sel-${item.roleApplicationId}`}>
                  <TalentAuditionCard audition={item} />
                </div>
              ))}
            </div>
          </div>
        )}

      </Container>
    </div>
  );
};

export default CastingDirectorProfile;